import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  mergeClasses,
  Dropdown,
  Option,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import {
  PersonRegular,
  SignOutRegular,
  ShieldTask24Regular,
} from "@fluentui/react-icons";
import { useMsal, useAccount } from "@azure/msal-react";
import { ApiError, getActiveEntity, setActiveEntity, useApiClient } from "../hooks/useApiClient";
import Logo from "./Logo";

const useStyles = makeStyles({
  container: {
    minHeight: "100vh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    zIndex: 1000,
    backgroundColor: tokens.colorBrandBackground, // Now uses custom brand color from theme
    padding: tokens.spacingVerticalM,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: tokens.shadow4,
    transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)", // Smoother easing and longer duration
    color: tokens.colorNeutralForegroundInverted,
  },
  headerScrolled: {
    backgroundColor: `color-mix(in srgb,${tokens.colorBrandBackground} 30%, transparent)`, // Slightly more opacity for better transition
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)", // For Safari support
    boxShadow: tokens.shadow8,
    color: tokens.colorNeutralForeground1,
  },
  headerTitle: {
    color: "inherit",
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalL,
    flex: 1,
  },
  nav: {
    display: "flex",
    gap: tokens.spacingHorizontalM,
    marginLeft: tokens.spacingHorizontalXXL,
  },
  navButton: {
    color: "inherit",
    "&:hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  userName: {
    color: "inherit",
  },
  signOutButton: {
    color: "inherit",
    "&:hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  main: {
    padding: tokens.spacingVerticalXL,
    paddingTop: "80px", // Account for fixed header height
    maxWidth: "1400px",
    margin: "0 auto",
  },
  logo: {
    height: "40px",
    marginRight: tokens.spacingHorizontalM,
    color: "inherit",
  },
});

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const navigate = useNavigate();
  const styles = useStyles();
  const apiClient = useApiClient();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  // Global admins only: the entity they work in and the entities to choose from.
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entities, setEntities] = useState<string[]>([]);

  const accountId = account?.homeAccountId;
  const username = account?.username;

  // Fetch user role on mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        let user;
        try {
          user = await apiClient.getCurrentUser();
        } catch (error) {
          // A selected entity the user may not access (e.g. from an email link): retry as themselves.
          if (!(error instanceof ApiError) || !getActiveEntity()) throw error;
          setActiveEntity(null);
          user = await apiClient.getCurrentUser();
        }
        setIsAdmin(user.isAdmin);
        if (!user.isGlobalAdmin) {
          setActiveEntity(null);
          return;
        }
        setEntityId(user.entityId);
        const settings = await apiClient.getAdminSettings();
        setEntities(settings.entities ?? [user.entityId]);
      } catch (error) {
        console.error("Failed to fetch user role:", error);
        if (error instanceof ApiError) {
          setAccessError(error.message);
          return;
        }
        // Fallback: check if username matches admin email
        setIsAdmin(username?.toLowerCase() === "admin@caseking.de");
      }
    };

    if (accountId) {
      fetchUserRole();
    }
    // Keyed on the account ID, not the account object, so it runs once per sign-in.
  }, [accountId, username, apiClient]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 20); // Increased threshold for more deliberate trigger
    };

    // Add scroll event listener
    window.addEventListener("scroll", handleScroll, { passive: true }); // Add passive for better performance

    // Check initial scroll position
    handleScroll();

    // Cleanup
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleEntityChange = (entity: string) => {
    setActiveEntity(entity);
    // Reload so every page refetches its data for the new entity.
    window.location.href = window.location.pathname.startsWith("/details/") ? "/admin" : window.location.pathname;
  };

  const handleSignOut = () => {
    instance.logoutPopup();
  };

  return (
    <div className={styles.container}>
      <header
        className={mergeClasses(
          styles.header,
          isScrolled && styles.headerScrolled
        )}
      >
        <Logo className={styles.logo} onClick={() => navigate("/")} style={{ cursor: "pointer" }} />
        <div className={styles.headerTitle}>
          <Text>Purchase Approval System</Text>
          <div className={styles.nav}>
            <Button appearance="subtle" className={styles.navButton} onClick={() => navigate("/my-requests")}>
              My requests
            </Button>
            {isAdmin && (
              <>
                <Button
                  appearance="subtle"
                  icon={<ShieldTask24Regular />}
                  className={styles.navButton}
                  onClick={() => navigate("/admin")}
                >
                  Admin Dashboard
                </Button>
                <Button appearance="subtle" className={styles.navButton} onClick={() => navigate("/admin/settings")}>
                  Settings
                </Button>
              </>
            )}
          </div>
        </div>
        <div className={styles.userSection}>
          {entityId && (
            <Dropdown
              aria-label="Entity"
              value={entityId}
              selectedOptions={[entityId]}
              onOptionSelect={(_, data) => data.optionValue && data.optionValue !== entityId && handleEntityChange(data.optionValue)}
              style={{ minWidth: "140px" }}
            >
              {entities.map((entity) => (
                <Option key={entity} value={entity}>{entity}</Option>
              ))}
            </Dropdown>
          )}
          <PersonRegular className={styles.userName} />
          <Text className={styles.userName}>
            {account?.name || account?.username || "User"}
          </Text>
          <Button
            appearance="subtle"
            icon={<SignOutRegular />}
            className={styles.signOutButton}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </div>
      </header>
      <main className={styles.main}>
        {accessError ? (
          <MessageBar intent="error"><MessageBarBody>{accessError}</MessageBarBody></MessageBar>
        ) : (
          children
        )}
      </main>
    </div>
  );
};

export default Layout;
